/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { useTenant } from "hooks/settings";
import { useCallback, useState } from "react";
import { useCreateEventsForReservationsMutation } from "src/store/api/generatedApi";
import { convertEventForApi } from "../utils/convertFromVideoTimestamps";
import { useNotifyStatus } from "contexts/status";
import { useTenantTranslation } from "hooks/formatters";
import { useNavigate } from "react-router";
import { useReservations } from "./useReservations";
import { useConfirmDialog } from "contexts/confirmDialog";
import { ScrapEyesEvent } from "components/scrapEyeLabelling/types";

export const useSubmitEvents = ({
  onSuccess,
  getElapsedSeconds,
  resetTimer,
}: {
  onSuccess?: () => void;
  getElapsedSeconds: () => number;
  resetTimer: () => void;
}) => {
  const tenantName = useTenant();
  const { t } = useTenantTranslation();
  const [createEvents] = useCreateEventsForReservationsMutation();
  const { reservations } = useReservations();
  const notifyStatus = useNotifyStatus();
  const navigate = useNavigate();
  const confirm = useConfirmDialog();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitEvents = useCallback(
    async (events: ScrapEyesEvent[]) => {
      if (isSubmitting) {
        return;
      }
      setIsSubmitting(true);
      const confirmed = await confirm({
        title: t("submitEvents"),
        message: t("areYouSureYouWantToSubmitEvents"),
      });
      if (!confirmed) {
        setIsSubmitting(false);
        return;
      }
      if (reservations?.[0] === undefined) {
        setIsSubmitting(false);
        return;
      }
      const activeLabellingSeconds = getElapsedSeconds();
      try {
        await createEvents({
          tenantName: tenantName,
          eventsCreate: {
            events: events.map(convertEventForApi),
            completed_labelling_reservations: reservations,
            active_labelling_seconds: activeLabellingSeconds,
          },
        }).unwrap();
        resetTimer();
        onSuccess?.();
        notifyStatus({
          type: "success",
          text: t("eventsSubmittedSuccessfully"),
        });
        await navigate(`/${tenantName}/tool/identify-events`);
      } catch {
        notifyStatus({
          type: "error",
          text: t("failedToSubmitEvents"),
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      createEvents,
      navigate,
      tenantName,
      t,
      onSuccess,
      getElapsedSeconds,
      resetTimer,
      reservations,
      confirm,
      isSubmitting,
      notifyStatus,
    ]
  );

  return { submitEvents, isSubmitting };
};
